import { TaxMode } from "shared/types/enum";

// Client-side mirror of spott-server's src/util/tax.ts — same formulas,
// kept in sync by convention. Used by the Upgrade modal's live "to pay"
// preview (against the business's CURRENT settings, pre-purchase) and the
// Invoice/Receipt PDF's Subtotal/Tax/Total breakdown (against a purchase's
// OWN snapshot, which is what actually determines what's shown).

/**
 * Decompose a price that already carries `rate`% tax (embedded, not added)
 * into its pre-tax subtotal and the tax portion. Works identically whether
 * `price` was inflated by additive tax at purchase time or left untouched
 * under inclusive tax — it only ever extracts what's already there. Amounts
 * are whole Rupiah, so the result is always rounded.
 */
export const decomposeTax = (
  price: number,
  rate: number,
): { subtotal: number; taxAmount: number } => {
  const taxAmount = Math.round((price * rate) / (100 + rate));
  return { subtotal: price - taxAmount, taxAmount };
};

/**
 * The price the customer would actually be charged, given the business's
 * CURRENT tax settings and a pre-tax base amount. Additive tax raises this
 * above `basePrice`; inclusive tax (or tax disabled) leaves it unchanged.
 */
export const applyPurchaseTax = (
  basePrice: number,
  business: { tax_enabled: boolean; tax_mode: TaxMode; tax_rate: number | null },
): number => {
  if (!business.tax_enabled || business.tax_mode !== TaxMode.Additive || !business.tax_rate) {
    return basePrice;
  }
  return Math.round((basePrice * (100 + business.tax_rate)) / 100);
};
